import {useMutation, useQuery, useQueryClient, type QueryKey} from '@tanstack/react-query';
import {ApiError} from '../../../api/http';

type UseCrudSectionParams<TQueryData, TSaveData> = {
    queryKey: QueryKey;
    proyectoId: number;
    getFn: (proyectoId: number) => Promise<TQueryData>;
    saveFn: (proyectoId: number, data: TSaveData) => Promise<unknown>;
    enabled?: boolean;
    onSuccess?: (savedData: TSaveData) => void | Promise<void>;
};

export function useCrudSection<TQueryData, TSaveData>({
                                                          queryKey,
                                                          proyectoId,
                                                          getFn,
                                                          saveFn,
                                                          enabled = true,
                                                          onSuccess,
                                                      }: UseCrudSectionParams<TQueryData, TSaveData>) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: [...queryKey, proyectoId],
        queryFn: () =>
            getFn(proyectoId).catch((err) => {
                if (err instanceof ApiError && err.status === 404) {
                    return null as TQueryData;
                }
                throw err;
            }),
        enabled: enabled && Number.isFinite(proyectoId) && proyectoId > 0,
    });

    const mutation = useMutation({
        mutationFn: (data: TSaveData) => saveFn(proyectoId, data),
        onSuccess: async (_result, variables) => {
            await queryClient.invalidateQueries({queryKey: [...queryKey, proyectoId], exact: true});
            await onSuccess?.(variables);
        },
    });

    return {
        ...query,
        save: mutation.mutateAsync,
        saveMutation: mutation,
    };
}
